import gql from 'graphql-tag'
import React, { useEffect } from 'react'
import { useLazyQuery, useMutation, useQuery } from 'react-apollo'
import { t } from 'src/lib/i18n-react'

import GoalSetting from 'src/pages/GoalSetting/GoalSetting'
import GoalSettingPlaceholder from 'src/pages/GoalSetting/GoalSettingPlaceholder'
import { CefrLevelCode, LanguageCode, Plan } from 'src/types'

const STUDY_GOALS = gql`
  query STUDY_GOALS($languageCode: LanguageEnum!) {
    currentUser {
      progress(code: $languageCode) {
        certGoalCefrLevel {
          code
          label
          description
        }
        liveLessonWeeklyGoal: weeklyGoals(kind: live_lesson) {
          progress
          final
          percent
          status
          startsAt
          endsAt
        }
        selfStudyWeeklyGoal: weeklyGoals(kind: minutes) {
          progress
          final
          percent
          status
          startsAt
          endsAt
        }
      }
    }
  }
`
//TODO: try to pass the goal level if possible, so we always know what level
//we are predicting against
const PREDICTED_COMPLETION_DATES = gql`
  query PREDICTED_COMPLETION_DATES($languageCode: LanguageEnum!) {
    predictedGoalCompletionDates(
      lessonCountMin: 1
      lessonCountMax: 14
      languageCode: $languageCode
    ) {
      liveLessonsPerWeek
      recommendedPlan {
        label
        credits
      }
      predictedGoalCompletionDate
    }
  }
`

const SAVE_STUDY_GOALS = gql`
  mutation SAVE_STUDY_GOALS(
    $languageCode: LanguageEnum!
    $liveLessonsCount: Int
    $selfStudyMinutes: Int
  ) {
    response: updateWeeklyGoals(
      languageCode: $languageCode
      liveLessonsCount: $liveLessonsCount
      selfStudyMinutes: $selfStudyMinutes
    ) {
      errors {
        code
        message
      }
    }
  }
`

type CompletionPredictions = {
  predictedGoalCompletionDates: {
    liveLessonsPerWeek: number
    recommendedPlan: Plan
    predictedGoalCompletionDate: string
  }[]
}

type WeeklyGoalsQueryData = {
  currentUser: {
    progress: {
      certGoalCefrLevel: {
        code: CefrLevelCode
        label: string
        description: string
      }
      liveLessonWeeklyGoal: goalData
      selfStudyWeeklyGoal: goalData
    }
  }
}

type goalData = {
  progress: number
  final: number
  percent: number
  status: string
  startsAt: string
  endsAt: string
}

type Props = {
  onShowSnackbar: ({}) => void
  l2Code: LanguageCode
}

const GoalsLazy: React.FC<Props> = ({ onShowSnackbar, l2Code }) => {
  const languageCode = LanguageCode[l2Code]

  const [getStudyGoals, { loading, error, data }] = useLazyQuery<
    WeeklyGoalsQueryData
  >(STUDY_GOALS, {
    variables: {
      languageCode,
    },
    fetchPolicy: 'network-only',
  })

  const {
    loading: getGoalPredictionsLoading,
    error: getGoalPredictionsError,
    data: getGoalPredictionsData,
  } = useQuery<CompletionPredictions>(PREDICTED_COMPLETION_DATES, {
    variables: {
      languageCode,
    },
    fetchPolicy: 'network-only',
  })

  useEffect(() => {
    getStudyGoals()
  }, [])

  const [saveGoals] = useMutation(SAVE_STUDY_GOALS)

  const handleSaveGoals = (
    liveLessonsCount?: number,
    selfStudyMinutes?: number
  ) => {
    try {
      saveGoals({
        variables: {
          languageCode,
          liveLessonsCount,
          selfStudyMinutes,
        },
      })
      onShowSnackbar({
        message: t('Your study goals were successfully updated.'),
        type: 'info',
      })
    } catch (e) {
      onShowSnackbar({
        message: t('Goals were not saved. Please refresh and try again.'),
        type: 'error',
      })
    }
  }

  if (loading) {
    return <GoalSettingPlaceholder />
  }

  if (data && getGoalPredictionsData) {
    const sideNoteData = getGoalPredictionsData?.predictedGoalCompletionDates
    const { currentUser } = data
    return (
      <GoalSetting
        cefrCode={currentUser.progress.certGoalCefrLevel.code}
        cefrLabel={currentUser.progress.certGoalCefrLevel.label}
        cefrDescription={currentUser.progress.certGoalCefrLevel.description}
        languageCode={languageCode}
        onSaveGoals={handleSaveGoals}
        sideNoteData={sideNoteData}
        liveLessonWeeklyGoal={currentUser.progress.liveLessonWeeklyGoal.final}
        selfStudyWeeklyGoal={currentUser.progress.selfStudyWeeklyGoal.final}
      />
    )
  }
  return null
}

export default GoalsLazy
